import { Component, EventEmitter, Input, OnInit, Output, signal, inject } from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  CustomValidators,
  InputError,
  SelectOption,
  inputErrorTypes,
  Icon, Currency, GroupedSelectOptions, NotificationEnum
} from '../../../global';
import {BehaviorSubject, combineLatest, concat, Observable, of} from 'rxjs';
import {debounceTime, map, shareReplay, tap} from 'rxjs/operators';
import {whereToSeeOptions} from '../../../../app/containers/registration/registration.config';
import { TranslateService, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { TextInputComponent } from '../../components/inputs/text-input/text-input.component';
import { AutocompleteInputComponent } from '../../components/inputs/text-autocomplete-input/autocomplete-input.component';
import { ButtonComponent } from '../../components/buttons/button/button.component';
import { ToggleComponent } from '../../components/inputs/toggle/toggle.component';
import { PasswordStrengthPresenterComponent } from '../../components/password-strength-presenter/password-strength-presenter.component';
import { MessageComponent } from '../../components/message/message.component';
import { GroupedSelectInputComponent } from '../../components/inputs/grouped-select-input/grouped-select-input.component';
import { SelectInputComponent } from '../../components/inputs/select-input/select-input.component';
import { CurrencyTransformPipe } from '../../pipes/currency-transform.pipe';

@Component({
    selector: 'slm-registration-form',
    templateUrl: './registration.form.html',
    imports: [NgFor, NgIf, TranslateDirective, TextInputComponent, AutocompleteInputComponent, ButtonComponent, ToggleComponent, PasswordStrengthPresenterComponent, MessageComponent, GroupedSelectInputComponent, SelectInputComponent, AsyncPipe, TranslatePipe, CurrencyTransformPipe]
})
export class RegistrationForm implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly translateService = inject(TranslateService);

  public readonly errors: Array<InputError> = [
    inputErrorTypes.required,
    inputErrorTypes.taxNumber,
    inputErrorTypes.minLength,
    inputErrorTypes.maxLength,
    inputErrorTypes.strongPassword,
    inputErrorTypes.email,
    inputErrorTypes.phone,
    inputErrorTypes.min,
    inputErrorTypes.max,
    inputErrorTypes.companyRegistrationNumber,
    inputErrorTypes.onlyNumbers
  ];
  public readonly icons = Icon;
  public readonly notificationType = NotificationEnum;
  public readonly whereToSeeOptions = whereToSeeOptions;
  public canAddMoreCompany = true;
  public registrationForm: UntypedFormGroup;
  public companyArray: UntypedFormArray = null;

  public passwordNotMatch$: Observable<boolean>;
  public finalPrice$: Observable<{price: number; currency: Currency}>;
  public showPackageWarning$ = new BehaviorSubject<boolean>(false);
  public showOtherDesc$: Observable<boolean>;
  public userCountHint$ = new BehaviorSubject<string>(null);
  public showPriceMessage = signal<boolean>(false);

  private maxCompanyCount: number;

  @Output() public readonly form = new EventEmitter<UntypedFormGroup>();
  @Input() public readonly taxNumberOptions: Array<SelectOption> = [];
  @Input() hideAuth = false;
  @Input() disableTaxNumberAutocomplete = false;
  @Input() subscriptions: Array<GroupedSelectOptions> = [];
  @Input() storageSubscriptions: Array<SelectOption> = [];
  @Input() single = false;

  public inputConfig = {
    password: {
      type: 'password',
      icon: Icon.VISIBILITY
    },
    passwordConfirmation: {
      type: 'password',
      icon: Icon.VISIBILITY
    }
  };

  ngOnInit() {
    this.registrationForm = this.fb.group({
      companies: this.fb.array([this.addCompanyForm()]),
      acceptedTerms: [false, [Validators.requiredTrue]],
      acceptedPlans: [false, [Validators.requiredTrue]],
      acceptedPolicy: [false, [Validators.requiredTrue]],
      ...(this.hideAuth
        ? {}
        : {
          email: ['', [Validators.required, Validators.email]],
          phone: ['', [Validators.required, CustomValidators.phone]],
          fullName: ['', [Validators.required]],
          password: [
            '',
            [
              Validators.required,
              CustomValidators.strongPassword,
              Validators.minLength(8)
            ]
          ],
          confirmPassword: [
            '',
            [
              Validators.required,
              CustomValidators.strongPassword,
              Validators.minLength(8)
            ]
          ],
          acceptedUserTerms: [false, [Validators.requiredTrue]],
          generalPackage: ['', [Validators.required]],
          storagePackage: ['', [Validators.required]],
          userCount: [{value: 0, disabled: true}, [Validators.min(0)]],
          invoiceEmail: ['', [Validators.required, Validators.email]],
          acceptTermsBy: ['', [Validators.required]],
          whereToSee: ['', [Validators.required]],
          otherDescription: [''],
          companyCounter: [{value: 0, disabled: true}],
          documentNumber: [0, [Validators.min(0), CustomValidators.onlyNumbers]],
          useDivision: [false]
        })
    });
    this.companyArray = this.registrationForm.get('companies') as UntypedFormArray;
    this.form.emit(this.registrationForm);
    const passwordControl = this.registrationForm.get('password');
    if (!this.hideAuth) {
      this.passwordNotMatch$ = combineLatest([
        this.registrationForm.get('confirmPassword').valueChanges,
        passwordControl.valueChanges
      ]).pipe(
        map((values) => !!values[0] && !!values[1] && values[0] !== values[1])
      );

      this.showOtherDesc$ = this.registrationForm
        .get('whereToSee').valueChanges.pipe(
          map(value => !!value.includes('other')),
          shareReplay({bufferSize: 1, refCount: true})
      );
    }

    const generalPackageControl = this.registrationForm.get('generalPackage');
    const userCountControl = this.registrationForm.get('userCount');
    const companyCounterControl = this.registrationForm.get('companyCounter');
    const documentNumberControl = this.registrationForm.get('documentNumber');
    const useDivisionControl = this.registrationForm.get('useDivision');
    const storagePackageControl = this.registrationForm.get('storagePackage');

    /**
     * Combining the package and user count control event values.
     */

    if (generalPackageControl && userCountControl) {
      this.finalPrice$ = combineLatest([
        concat(of(null), generalPackageControl.valueChanges),
        concat(of(0), userCountControl.valueChanges),
        concat(of(0), companyCounterControl.valueChanges),
        concat(of(0), documentNumberControl.valueChanges),
        concat(of(false), useDivisionControl.valueChanges),
        concat(of(''), storagePackageControl.valueChanges)
      ]).pipe(
        debounceTime(250),
        tap(([subscription, _, __, ___, ____, _____]) => {
          this.maxCompanyCount = subscription?.maxPartnerInGroup;
          this.checkToAddMoreCompany();

          if(subscription?.maxFreeUser){
            const label = this.translateService.instant('AUTHENTICATION.REGISTRATION.DEFAULT_USER',
              {freeUserNumber: subscription?.maxFreeUser});
            this.userCountHint$.next(label);
          }else{
            this.userCountHint$.next(null);
          }

          if(subscription?.maxUser){
            userCountControl.clearValidators();
            userCountControl.addValidators([Validators.min(0), Validators.max(subscription?.maxUser)])
            userCountControl.updateValueAndValidity({emitEvent: false});
            subscription?.maxUser === 1 ? userCountControl.disable({emitEvent: false}) : userCountControl.enable({emitEvent: false});
          }
        }),
        map(
          ([subscription, userCount, companyCount, documentCount, useDivision, storagePackage]: [any, any, any, any, any, any]) => {
            const baseFee = +subscription?.baseFee;
            const userFee = (+subscription?.userFee) * (+userCount);
            const companyFee = companyCount ? (+subscription?.companyFee * +companyCount) : 0
            const storageFee = !!storagePackage?.fee ? +storagePackage.fee : 0 ;
            const divisionFee = !!useDivision ? +subscription?.divisionFee : 0;

            const payableDocumentCount = +documentCount - (+subscription?.freeDocuments);
            const documentFee = !!documentCount && payableDocumentCount > 0 ? (payableDocumentCount * +subscription?.documentFee) : 0;

            if (companyFee + documentFee + storageFee + divisionFee > 0) {
              this.showPriceMessage.set(true);
            } else {
              this.showPriceMessage.set(false);
            }

            return ({
              price: baseFee +
                userFee +
                companyFee +
                documentFee +
                storageFee +
                divisionFee,
              currency: subscription?.currency
            });
          }
        ),
        shareReplay({
          refCount: true,
          bufferSize: 1
        })
      );
    }
  }

  public passwordTypeChanged(type: string): void {
    const isPassword = this.inputConfig[type]?.type === 'password';
    this.inputConfig[type] = {
      type: isPassword ? 'text' : 'password',
      icon: isPassword ? Icon.VISIBILITY_OFF : Icon.VISIBILITY
    };
  }

  private addCompanyForm(): UntypedFormGroup {
    return this.fb.group({
      registrationNumber: ['', [Validators.required, CustomValidators.companyRegistrationNumber]],
      companyName: ['', [Validators.required]],
      taxNumber: ['', [Validators.required, CustomValidators.taxNumber]],
      city: ['', [Validators.required]],
      zipCode: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(16)
        ]
      ],
      street: ['', [Validators.required]],
      address: ['', [Validators.required]]
    });
  }

  public addCompany() {
    const companyFormArray = this.registrationForm.get('companies') as UntypedFormArray;
    if (companyFormArray.length >= this.maxCompanyCount || this.single) {
      return;
    }
    companyFormArray.push(this.addCompanyForm());
    this.checkToAddMoreCompany();
    this.setCompanyCounter();
  }

  public removeCompany(index: number) {
    if (this.single) {
      return;
    }
    const companyFormArray = this.registrationForm.get('companies') as UntypedFormArray;
    if (companyFormArray.length === 1) {
      return;
    }
    companyFormArray.removeAt(index);
    this.checkToAddMoreCompany();
    this.setCompanyCounter();
  }

  private checkToAddMoreCompany(){
    const companyFormArray = this.registrationForm.get('companies') as UntypedFormArray;
    this.canAddMoreCompany = companyFormArray.length < this.maxCompanyCount;
  }

  private setCompanyCounter(){
    const companyFormArray = this.registrationForm.get('companies') as UntypedFormArray;
    this.registrationForm.get('companyCounter').setValue(companyFormArray.length - 1);
  }
}
